import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

/*
 * /download — landing shown when a QR scanner is on a desktop OR when
 * the native apps aren't published yet. Shows both App Store + Play badges.
 * Passes through the `?biz=X&h=N` params to the web fallback so scan
 * attribution keeps working.
 *
 * When apps go live: set IOS_APP_STORE_URL + ANDROID_PLAY_STORE_URL in
 * backend env vars; mobile QR scanners get sent straight to the stores
 * and never see this page.
 */

// Placeholder store URLs until published. Empty = show "Coming soon" pill.
const IOS_URL = "";
const PLAY_URL = "";

export default function Download() {
  const search = useLocation().search;
  return (
    <div className="min-h-[calc(100vh-56px)] flex items-center">
      <div className="mx-auto max-w-[720px] px-6 py-16 md:px-12 md:py-24">
        <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
          → get the app
        </p>
        <h1 className="mt-6 text-5xl leading-[1.02] tracking-tight md:text-6xl">
          Book Away
          <br />
          <span className="slogan gold-metallic-text">on your phone.</span>
        </h1>
        <p className="mt-6 max-w-lg text-base text-muted-foreground md:text-lg">
          Same-day openings at your favourite salons, one tap away.
          Push alerts on new slots, saved businesses, one-tap booking.
        </p>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
          <StoreBadge
            data-testid="download-ios-btn"
            label="App Store"
            sub="iPhone & iPad"
            href={IOS_URL}
          />
          <StoreBadge
            data-testid="download-android-btn"
            label="Google Play"
            sub="Android"
            href={PLAY_URL}
          />
        </div>

        <div className="mt-10 border-t border-border pt-6">
          <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            → or use the web version now
          </p>
          <Link
            to={`/${search}`}
            data-testid="download-web-cta"
            className="mt-3 inline-flex items-center gap-2 text-sm underline decoration-[#C9A85F] underline-offset-4 hover:text-[#8A7333]"
          >
            Continue in your browser <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function StoreBadge({ label, sub, href, ...rest }) {
  const disabled = !href;
  const inner = (
    <div
      className={`flex items-center gap-3 border px-4 py-3 transition-colors ${
        disabled
          ? "border-border text-muted-foreground cursor-not-allowed"
          : "border-foreground hover:bg-foreground hover:text-background"
      }`}
    >
      <Sparkles size={18} className={disabled ? "text-muted-foreground" : "text-[#C9A85F]"} />
      <div className="flex flex-col text-left">
        <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground">
          {disabled ? "coming soon" : "download on"}
        </span>
        <span className="text-sm font-semibold">{label}</span>
        <span className="mono text-[9px] text-muted-foreground">{sub}</span>
      </div>
    </div>
  );
  if (disabled) return <div {...rest}>{inner}</div>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {inner}
    </a>
  );
}
